import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGeo } from "@/context/GeoContext";
import { buildAffiliateHref } from "@/lib/affiliate";
import { resolveDisplay, formatPrice } from "@/lib/currency";

export default function HotelOptionCard({ hotel }) {
    const { geo, currencyPref } = useGeo();
    const { t } = useTranslation();
    const region = geo?.affiliate_region || "WORLD";
    const {
        id,
        name,
        city,
        star_rating,
        image_url,
        price_per_night,
        display_price,
        rating_score,
        review_count,
        amenities = [],
        book_url,
        is_grounded,
        source_label,
        confidence,
        in_region_fallbacks,
    } = hotel;

    const { display, original, sameCurrency } = resolveDisplay(
        price_per_night,
        display_price,
        currencyPref,
    );

    return (
        <article
            className="relative w-[280px] shrink-0 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white transition hover:border-[#0A0A0A]"
            data-testid={`hotel-option-${id}`}
        >
            {/* Green dot top-right (Hotels) */}
            <span
                aria-hidden
                className="absolute right-3 top-3 z-10 inline-block h-2.5 w-2.5 rounded-full bg-[#16A34A] ring-4 ring-[#16A34A]/15"
                data-testid="card-color-dot"
            />

            <div className="aspect-[16/10] w-full overflow-hidden bg-[#F5F5F5]">
                {image_url ? (
                    <img
                        src={image_url}
                        alt={name}
                        loading="lazy"
                        className="h-full w-full object-cover transition group-hover:scale-105"
                    />
                ) : null}
            </div>

            <div className="p-4">
                <h3 className="line-clamp-1 text-[15px] font-semibold tracking-tight text-[#0A0A0A]">
                    {name}
                </h3>
                <div className="mt-1 flex items-center gap-1.5">
                    <div className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                                key={i}
                                size={11}
                                strokeWidth={1.5}
                                className={
                                    i < star_rating
                                        ? "fill-[#0A0A0A] text-[#0A0A0A]"
                                        : "text-[#E5E5E5]"
                                }
                            />
                        ))}
                    </div>
                    <span className="text-[11px] text-[#A3A3A3]">·</span>
                    <span className="text-[11px] text-[#737373]">{city}</span>
                </div>

                {rating_score !== undefined && (
                    <div className="mt-2 flex items-center gap-1.5">
                        <span className="rounded-md bg-[#0A0A0A] px-1.5 py-0.5 text-[11px] font-semibold text-white">
                            {rating_score.toFixed(1)}
                        </span>
                        <span className="text-[11px] text-[#737373]">
                            {review_count?.toLocaleString()} reviews
                        </span>
                    </div>
                )}

                {amenities.length > 0 && (
                    <div className="mt-3 line-clamp-1 text-[11px] text-[#737373]">
                        {amenities.slice(0, 3).join(" · ")}
                    </div>
                )}

                <div className="mt-4 flex items-end justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                            per night
                        </div>
                        <div
                            className="text-[18px] font-semibold leading-none tracking-tight text-[#0A0A0A]"
                            data-testid="hotel-price"
                        >
                            {formatPrice(display)}
                        </div>
                        {!sameCurrency && (currencyPref || "USD").toUpperCase() === "USD" && (
                            <div
                                className="mt-0.5 text-[10px] text-[#A3A3A3]"
                                data-testid="hotel-price-original"
                            >
                                {formatPrice(original)} {original.currency}
                            </div>
                        )}
                        {is_grounded && confidence !== "high" && (
                            <div
                                className="mt-0.5 text-[10px] italic text-[#A3A3A3]"
                                data-testid="hotel-price-approx"
                            >
                                approx
                            </div>
                        )}
                    </div>
                    {!!book_url && (
                        <a
                            href={is_grounded ? book_url : buildAffiliateHref(book_url, region)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`book-now-${id}`}
                            className="inline-flex items-center justify-center rounded-lg bg-[#0A0A0A] px-4 py-2 text-[12px] font-medium text-white transition hover:bg-[#262626]"
                        >
                            {is_grounded ? t("cards.view_on", { source: source_label || "Web" }) : t("cards.book_now")}
                        </a>
                    )}
                </div>

                {/* Phase 6.7 — IN-region MMT-primary + 2 fallbacks. Renders
                    only when backend tagged the card with `in_region_fallbacks`. */}
                {Array.isArray(in_region_fallbacks) && in_region_fallbacks.length > 0 && (
                    <div
                        className="mt-3 flex items-center gap-1.5 text-[11px] text-[#737373]"
                        data-testid={`in-region-fallbacks-${id}`}
                    >
                        <span>Not on MakeMyTrip?</span>
                        <span className="text-[#A3A3A3]">·</span>
                        <span className="inline-flex items-center gap-2">
                            {in_region_fallbacks.map((fb, i) => (
                                <span key={fb.slug} className="inline-flex items-center gap-2">
                                    <a
                                        href={fb.book_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`in-region-fallback-${fb.slug}-${id}`}
                                        className="inline-block py-1 underline-offset-2 hover:underline text-[#0A0A0A]"
                                        style={{ minHeight: 32, display: "inline-flex", alignItems: "center" }}
                                    >
                                        {fb.label}
                                    </a>
                                    {i < in_region_fallbacks.length - 1 && (
                                        <span className="text-[#A3A3A3]">·</span>
                                    )}
                                </span>
                            ))}
                        </span>
                    </div>
                )}
            </div>
        </article>
    );
}
